import React, { useState, useEffect } from 'react';
import { 
  FileText, Calendar, CheckCircle, XCircle, Clock, RefreshCw, User, Edit3, 
  AlertCircle, CheckSquare, RotateCcw, FileCheck, Download, Trash2, Upload, 
  Info, FileCode, FileEdit, Printer, Search
} from 'lucide-react';
import api from '../services/api';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import TemplateManagementModal from '../components/TemplateManagementModal';
import InAppTemplateEditorModal from '../components/InAppTemplateEditorModal';
import { buildDepartmentOptions, getUserDepartmentNames, userMatchesDepartment } from '../utils/departments';
import { printAttestation } from '../utils/documentTemplates';
import { fetchDocumentTypes, documentTypeName } from '../utils/documentTypes';
import './AllRequests.css';

const DEFAULT_TEMPLATE_TYPES = [
  { type: 'work_certificate', name: 'Work Certificate (Attestation de Travail)', label: 'Attestation de Travail', desc: 'Standard certificate of employment for active team members' },
  { type: 'salary_certificate', name: 'Salary Certificate (Attestation de Salaire)', label: 'Attestation de Salaire', desc: 'Official salary confirmation document' },
  { type: 'employment_certificate', name: 'Employment Certificate (Attestation d\'Emploi)', label: "Attestation d'Emploi", desc: 'Certificate stating active role and department' },
  { type: 'resignation_certificate', name: 'Resignation Certificate (Attestation de Démission)', label: 'Attestation de Démission', desc: 'End of service certificate for departed staff' },
  { type: 'internship_certificate', name: 'Internship Certificate (Attestation de Stage)', label: 'Attestation de Stage', desc: 'Official internship completion certificate' },
];

const AllRequests = () => {
  const [attestationRequests, setAttestationRequests] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [customRequests, setCustomRequests] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [checkingLeaveBalanceId, setCheckingLeaveBalanceId] = useState(null);
  const [leaveBalancePopup, setLeaveBalancePopup] = useState(null);
  const [activeTab, setActiveTab] = useState('attestations');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [referenceFilter, setReferenceFilter] = useState('all');
  const [versionFilter, setVersionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');

  // Custom Request Approval Modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approvalFile, setApprovalFile] = useState(null);

  // Document Template Modals
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showInAppEditorModal, setShowInAppEditorModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  // Document types (names in both languages, removed ones) and the rename dialog
  const [documentTypes, setDocumentTypes] = useState([]);
  const [renamingType, setRenamingType] = useState(null); // { type, nameFr, nameEn }

  // Admin Fulfill / Visual Editor Modal
  const [showFulfillModal, setShowFulfillModal] = useState(false);

  useMarkAsReadOnMount(['attestation', 'leave', 'custom_request']);

  useEffect(() => {
    fetchAllRequests();
  }, []);

  const fetchAllRequests = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const [attestations, leaves, customs, tList, depts, docTypes] = await Promise.all([
        api.get('/attestations'),
        api.get('/absences'),
        api.get('/requests'),
        api.get('/attestations/templates').catch(() => []),
        api.get('/departments').catch(() => []),
        fetchDocumentTypes(),
      ]);
      setDocumentTypes(docTypes);
      setAttestationRequests(attestations || []);
      setLeaveRequests(leaves || []);
      setCustomRequests(customs || []);
      setTemplatesList(Array.isArray(tList) ? tList : []);
      setDepartmentsList(Array.isArray(depts) ? depts : []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setMessage({ type: 'error', text: 'Error loading requests.' });
    } finally {
      setLoading(false);
    }
  };

  // The editor loads the template and the employee's data itself
  const openFulfillModal = (request) => {
    setSelectedRequest(request);
    setShowFulfillModal(true);
  };

  const closeFulfillModal = () => {
    setShowFulfillModal(false);
    setSelectedRequest(null);
  };

  // Prints the generated PDF itself, so the sheet is identical to the download
  const handlePrintAttestation = async (request) => {
    try {
      await printAttestation(request.id);
    } catch (error) {
      console.error('Error preparing print:', error);
      setMessage({ type: 'error', text: `Error printing certificate: ${error.message}` });
    }
  };

  const handleDownloadAttestation = async (request) => {
    try {
      await api.downloadAttestation(request.id, `${request.attestationType}_${request.user?.lastName || request.id}`);
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessage({ type: 'error', text: `Error downloading certificate: ${error.message}` });
    }
  };

  const handleDownloadTemplate = async (type, originalName, lang = 'fr') => {
    try {
      const blob = await api.downloadBlob(`/attestations/templates/${type}/download?lang=${lang}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || `template_${type}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to download template file. ' + (err.message || '') });
    }
  };

  const handleDeleteTemplate = async (type) => {
    const name = documentTypeName(documentTypes, type, 'fr');
    if (!window.confirm(`Reset the template of "${name}" (French and English)? The built-in template will be used again. The document itself stays in the list.`)) {
      return;
    }

    try {
      await api.delete(`/attestations/templates/${type}`);
      setMessage({ type: 'success', text: `Template of "${name}" reset to the built-in one.` });
      fetchAllRequests();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reset template: ' + (err.message || '') });
    }
  };

  const handleRenameType = async () => {
    if (!renamingType) return;
    const nameFr = renamingType.nameFr.trim();
    const nameEn = renamingType.nameEn.trim();
    if (!nameFr || !nameEn) {
      setMessage({ type: 'error', text: 'Please fill in both the French and the English name.' });
      return;
    }
    try {
      const types = await api.patch(`/attestations/types/${renamingType.type}`, { nameFr, nameEn });
      if (Array.isArray(types)) setDocumentTypes(types);
      setRenamingType(null);
      setMessage({ type: 'success', text: `Document renamed: "${nameFr}" / "${nameEn}".` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to rename the document: ' + (err.message || '') });
    }
  };

  const handleRemoveType = async (docType) => {
    if (!window.confirm(`Remove "${docType.nameFr}" / "${docType.nameEn}"?\n\nEmployees will no longer be able to request it. Requests already made are kept, and you can bring it back later.`)) {
      return;
    }
    try {
      const types = await api.delete(`/attestations/types/${docType.type}`);
      if (Array.isArray(types)) setDocumentTypes(types);
      else fetchAllRequests();
      setMessage({ type: 'success', text: `"${docType.nameFr}" was removed from the documents employees can request.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove the document: ' + (err.message || '') });
    }
  };

  const handleRestoreType = async (docType) => {
    try {
      const types = await api.post(`/attestations/types/${docType.type}/restore`, {});
      if (Array.isArray(types)) setDocumentTypes(types);
      setMessage({ type: 'success', text: `"${docType.nameFr}" can be requested again.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to restore the document: ' + (err.message || '') });
    }
  };

  const isAnnualLeaveType = (request) => {
    const type = String(request?.type || request?.leaveType || '').toLowerCase().trim();
    return ['vacation', 'annual_leave', 'annual leave'].includes(type);
  };

  const handleCheckLeaveBalance = async (request) => {
    const userId = request?.userId || request?.user?.id;
    if (!userId) {
      setMessage({ type: 'error', text: 'Unable to identify employee for leave balance check.' });
      return;
    }

    setCheckingLeaveBalanceId(request.id);
    try {
      const balance = await api.get(`/absences/leave-balance/user/${userId}`);
      setLeaveBalancePopup({
        requestId: request.id,
        balance,
        error: null,
      });
      setTimeout(() => setLeaveBalancePopup(null), 5000);
    } catch (error) {
      setLeaveBalancePopup({
        requestId: request.id,
        balance: null,
        error: error.message || 'Unable to load leave balance',
      });
      setTimeout(() => setLeaveBalancePopup(null), 5000);
    } finally {
      setCheckingLeaveBalanceId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const handleAction = async (request, actionType) => {
    if (actionType === 'approve' && (request.status === 'approved' || request.status === 'generated')) {
      setMessage({ type: 'error', text: 'Request is already approved' });
      return;
    }
    if (actionType === 'reject' && request.status === 'rejected') {
      setMessage({ type: 'error', text: 'Request is already rejected' });
      return;
    }

    if (activeTab === 'customs' && actionType === 'approve') {
      setSelectedRequest(request);
      setShowApprovalModal(true);
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      let endpoint = '';
      let data = {};

      if (activeTab === 'attestations') {
        if (actionType === 'approve') {
          endpoint = `/attestations/${request.id}/approve`;
          data = {};
        } else if (actionType === 'reject') {
          endpoint = `/attestations/${request.id}/reject`;
          data = { rejectionReason: 'Rejected by admin' };
        } else if (actionType === 'revoke') {
          endpoint = `/attestations/${request.id}/revoke`;
          data = {};
        }
        await api.patch(endpoint, data);
      } else if (activeTab === 'leaves') {
        let status = actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'pending';
        endpoint = `/absences/${request.id}/status`;
        const data = { status };
        if (actionType === 'reject') {
          data.rejectionReason = 'Rejected by admin';
        }
        await api.patch(endpoint, data);
      } else if (activeTab === 'customs') {
        let status = actionType === 'approve' ? 'approved' : actionType === 'reject' ? 'rejected' : 'pending';
        endpoint = `/requests/${request.id}/status`;
        const data = { status };
        if (actionType === 'reject') {
          data.rejectionReason = 'Rejected by admin';
        }
        await api.patch(endpoint, data);
      }

      setMessage({ type: 'success', text: `Request ${actionType}d successfully!` });
      fetchAllRequests();
    } catch (error) {
      console.error('Error processing request:', error);
      setMessage({ type: 'error', text: `Error ${actionType}ing request: ${error.message || 'Unknown error'}` });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveCustomRequest = async () => {
    if (!selectedRequest) return;

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const fd = new FormData();
      fd.append('status', 'approved');
      if (approvalMessage) fd.append('notes', approvalMessage);
      if (approvalFile) fd.append('file', approvalFile);

      await api.upload('PATCH', `/requests/${selectedRequest.id}/status`, fd);

      setMessage({ type: 'success', text: 'Request approved successfully!' });
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setApprovalMessage('');
      setApprovalFile(null);
      fetchAllRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      setMessage({ type: 'error', text: `Error approving request: ${error.message || 'Unknown error'}` });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'generated':
        return <CheckCircle size={18} className="status-icon success" />;
      case 'rejected':
        return <XCircle size={18} className="status-icon error" />;
      case 'pending':
        return <Clock size={18} className="status-icon pending" />;
      case 'revoked':
        return <RotateCcw size={18} className="status-icon revoked" />;
      default:
        return <AlertCircle size={18} className="status-icon" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'pending':
        return 'status-pending';
      case 'generated':
        return 'status-generated';
      case 'revoked':
        return 'status-revoked';
      default:
        return '';
    }
  };

  const renderAttestationRequests = () => {
    if (attestationRequests.length === 0) {
      return (
        <div className="empty-state">
          <FileText size={64} />
          <h3>No Attestation Requests</h3>
          <p>No attestation requests found.</p>
        </div>
      );
    }

    // Collect filter dropdown options
    const availableReferences = Array.from(new Set([
      ...attestationRequests.map(r => r.reference || r.template?.reference).filter(Boolean),
      ...templatesList.map(t => t.reference).filter(Boolean)
    ]));
    const defaultRefs = [
      'Réf : ATT-PS - VER/REV',
      'Réf : ATT-AT - VER/REV',
      'Réf : ATT-AS - VER/REV',
      'Réf : ATT-AE - VER/REV',
      'Réf : ATT-AD - VER/REV'
    ];
    defaultRefs.forEach(defRef => {
      if (!availableReferences.includes(defRef)) {
        availableReferences.push(defRef);
      }
    });

    const availableVersions = Array.from(new Set(
      attestationRequests.map(r => r.version || r.template?.version || 'V1/R0').filter(Boolean)
    ));
    if (!availableVersions.includes('V1/R0')) {
      availableVersions.unshift('V1/R0');
    }

    const userMap = new Map();
    attestationRequests.forEach(r => {
      if (r.user && r.user.id) {
        const name = `${r.user.firstName || ''} ${r.user.lastName || ''}`.trim() || r.user.email;
        userMap.set(String(r.user.id), name);
      }
    });
    const availableUsers = Array.from(userMap.entries()).map(([id, name]) => ({ id, name }));

    const monthMap = new Map();
    attestationRequests.forEach(r => {
      if (r.createdAt) {
        const d = new Date(r.createdAt);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        monthMap.set(monthKey, monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1));
      }
    });
    const availableMonths = Array.from(monthMap.entries()).map(([key, label]) => ({ key, label }));

    const availableDepartments = buildDepartmentOptions(
      departmentsList,
      attestationRequests.map(r => r.user),
    );

    const isFilterActive = searchTerm || statusFilter !== 'all' || referenceFilter !== 'all' || versionFilter !== 'all' || userFilter !== 'all' || monthFilter !== 'all' || departmentFilter !== 'all';

    const handleResetFilters = () => {
      setSearchTerm('');
      setStatusFilter('all');
      setReferenceFilter('all');
      setVersionFilter('all');
      setUserFilter('all');
      setMonthFilter('all');
      setDepartmentFilter('all');
    };

    const filteredAttestations = attestationRequests.filter(req => {
      const ref = req.reference || req.template?.reference || 'Réf : ATT-PS - VER/REV';
      const ver = req.version || req.template?.version || 'V1/R0';
      const reqUserId = req.user?.id ? String(req.user.id) : '';
      const reqDept = getUserDepartmentNames(req.user).join(' ');

      let reqMonth = '';
      if (req.createdAt) {
        const d = new Date(req.createdAt);
        reqMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      }

      const matchesSearch = 
        req.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ver.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reqDept.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
      const matchesRef = referenceFilter === 'all' || ref === referenceFilter;
      const matchesVer = versionFilter === 'all' || ver === versionFilter;
      const matchesUser = userFilter === 'all' || reqUserId === String(userFilter);
      const matchesMonth = monthFilter === 'all' || reqMonth === monthFilter;
      const matchesDept = userMatchesDepartment(req.user, departmentFilter);

      return matchesSearch && matchesStatus && matchesRef && matchesVer && matchesUser && matchesMonth && matchesDept;
    });

    return (
      <div className="requests-table-container">
        {/* Comprehensive Filter Bar */}
        <div className="table-filters-bar" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center', padding: '14px 16px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <div className="search-container" style={{ flex: '1 1 200px', minWidth: '180px', display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px 12px' }}>
            <Search size={16} style={{ color: '#94a3b8', marginRight: '8px', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search employee, email, Réf, version..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '13px' }}
            />
          </div>

          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="all">Tous les Employés</option>
            {availableUsers.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="all">Tous les Mois</option>
            {availableMonths.map(m => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>

          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="all">Tous les Départements</option>
            {availableDepartments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>

          <select
            value={referenceFilter}
            onChange={(e) => setReferenceFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff', fontWeight: '600', color: '#1d4ed8' }}
          >
            <option value="all">Toutes les Références (Réf)</option>
            {availableReferences.map(ref => (
              <option key={ref} value={ref}>
                {ref.toLowerCase().startsWith('réf') || ref.toLowerCase().startsWith('ref') ? ref : `Réf: ${ref}`}
              </option>
            ))}
          </select>

          <select
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff', fontWeight: '600', color: '#475569' }}
          >
            <option value="all">Toutes les Versions (Ver)</option>
            {availableVersions.map(ver => (
              <option key={ver} value={ver}>Ver: {ver}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#ffffff', color: '#1e293b' }}
          >
            <option value="all">Tous les Statuts</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="generated">Generated</option>
          </select>

          {isFilterActive && (
            <button
              onClick={handleResetFilters}
              className="btn btn-secondary btn-sm"
              style={{ fontSize: '12px', padding: '7px 12px', background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              Reset Filters
            </button>
          )}
        </div>

        {filteredAttestations.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px', textAlign: 'center' }}>
            <FileText size={48} style={{ color: '#94a3b8', marginBottom: '12px' }} />
            <h4>No requests match the selected Reference / Version / Search filter</h4>
          </div>
        ) : (
          <table className="requests-table">
            <colgroup>
              <col style={{ width: '4%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '13%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>ID</th>
                <th>Employee</th>
                <th>Type</th>
                <th>RÉFÉRENCE & VERSION</th>
                <th>Status</th>
                <th>Purpose</th>
                <th>Created</th>
                <th>Approved By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttestations.map((request) => (
                <tr key={`att-${request.id}`} className={getStatusClass(request.status)}>
                  <td className="id-cell">#{request.id}</td>
                  <td className="employee-cell">
                    <div className="employee-info">
                      <User size={16} />
                      <div>
                        <div className="employee-name">{request.user?.firstName} {request.user?.lastName}</div>
                        <div className="employee-email">{request.user?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="type-cell">
                    {documentTypeName(documentTypes, request.attestationType, request.language || 'fr')}
                    <span style={{ marginLeft: 6, fontSize: '0.7rem', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 }}>
                      {String(request.language || 'fr').toUpperCase()}
                    </span>
                  </td>
                  <td className="reference-cell">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontSize: '0.75rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '2px 7px', borderRadius: '5px', fontWeight: 700, width: 'fit-content' }}>
                        {request.reference || request.template?.reference || 'Réf : ATT-PS - VER/REV'}
                      </span>
                      <span style={{ fontSize: '0.7rem', background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '1px 6px', borderRadius: '4px', fontWeight: 600, width: 'fit-content' }}>
                        Ver: {request.version || request.template?.version || 'V1/R0'}
                      </span>
                    </div>
                  </td>
                  <td className="status-cell">
                    <span className={`status-badge ${getStatusClass(request.status)}`}>
                      {getStatusIcon(request.status)}
                      {request.status}
                    </span>
                  </td>
                  <td className="purpose-cell">{request.purpose || '-'}</td>
                  <td className="date-cell">
                    <Calendar size={14} />
                    {formatDate(request.createdAt)}
                  </td>
                  <td className="approved-by-cell">
                    {request.approvedBy ? (
                      <>{request.approvedBy.firstName} {request.approvedBy.lastName}</>
                    ) : '-'}
                  </td>
                  <td className="actions-cell">
                    <div className="action-buttons" style={{ flexWrap: 'wrap', gap: '6px' }}>
                      {request.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => openFulfillModal(request)}
                            disabled={submitting}
                            title="Générer et Approuver avec données de l'employé"
                          >
                            Générer & Approuver
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleAction(request, 'reject')}
                            disabled={submitting}
                          >
                            <XCircle size={14} />
                            Reject
                          </button>
                        </>
                      )}

                      {(request.status === 'approved' || request.status === 'generated') && (
                        <>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => openFulfillModal(request)}
                            disabled={submitting}
                            title="Remplir & Générer la version personnalisée"
                          >
                            {request.status === 'generated' ? 'Modifier' : 'Générer'}
                          </button>

                          {request.status === 'generated' && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handlePrintAttestation(request)}
                              title="Imprimer l'attestation"
                            >
                              <Printer size={14} />
                              Imprimer
                            </button>
                          )}

                          {request.status === 'generated' && request.generatedFilePath && (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleDownloadAttestation(request)}
                              title="Télécharger PDF"
                            >
                              <Download size={14} />
                              PDF
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderLeaveRequests = () => {
    if (leaveRequests.length === 0) {
      return (
        <div className="empty-state">
          <Calendar size={64} />
          <h3>No Leave Requests</h3>
          <p>No leave requests found.</p>
        </div>
      );
    }

    return (
      <div className="requests-table-container">
        <table className="requests-table">
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '17%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>ID</th>
              <th>Employee</th>
              <th>Type</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Reason</th>
              <th>Created</th>
              <th>Approved By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaveRequests.map((request) => (
              <tr key={`leave-${request.id}`} className={getStatusClass(request.status)}>
                <td className="id-cell">#{request.id}</td>
                <td className="employee-cell">
                  <div className="employee-info">
                    <User size={16} />
                    <div>
                      <div className="employee-name">{request.user?.firstName} {request.user?.lastName}</div>
                      <div className="employee-email">{request.user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="type-cell">{request.type || request.leaveType}</td>
                <td className="status-cell">
                  <span className={`status-badge ${getStatusClass(request.status)}`}>
                    {getStatusIcon(request.status)}
                    {request.status}
                  </span>
                </td>
                <td className="date-cell">
                  {formatDate(request.startDate)} - {formatDate(request.endDate)}
                </td>
                <td className="purpose-cell">{request.reason || '-'}</td>
                <td className="date-cell">
                  <Calendar size={14} />
                  {formatDate(request.createdAt)}
                </td>
                <td className="approved-by-cell">
                  {request.approvedBy ? (
                    <>{request.approvedBy.firstName} {request.approvedBy.lastName}</>
                  ) : '-'}
                </td>
                <td className="actions-cell">
                  {(request.status === 'pending' || request.status === 'revoked') && (
                    <div className="action-buttons">
                      {request.status === 'pending' && isAnnualLeaveType(request) && (
                        <div className="leave-balance-button-wrapper">
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => handleCheckLeaveBalance(request)}
                            disabled={checkingLeaveBalanceId === request.id}
                            title="Consult real-time leave balance"
                          >
                            <Info size={14} />
                            {checkingLeaveBalanceId === request.id ? 'Checking...' : 'Solde'}
                          </button>
                          {leaveBalancePopup?.requestId === request.id && (
                            <div className="leave-balance-popover">
                              {leaveBalancePopup.error ? (
                                <p className="error-text">{leaveBalancePopup.error}</p>
                              ) : (
                                <div>
                                  <strong>Solde Congé ({leaveBalancePopup.balance?.year})</strong>
                                  <div>Droit: {leaveBalancePopup.balance?.earnedDays} j</div>
                                  <div>Pris: {leaveBalancePopup.balance?.usedDays} j</div>
                                  <div className="highlight">Solde: {leaveBalancePopup.balance?.availableDays} j</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleAction(request, 'approve')}
                        disabled={submitting}
                      >
                        <CheckSquare size={14} />
                        Approve
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleAction(request, 'reject')}
                        disabled={submitting}
                      >
                        <XCircle size={14} />
                        Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCustomRequests = () => {
    if (customRequests.length === 0) {
      return (
        <div className="empty-state">
          <Edit3 size={64} />
          <h3>No Custom Requests</h3>
          <p>No custom requests found.</p>
        </div>
      );
    }

    return (
      <div className="requests-table-container">
        <table className="requests-table">
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '17%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>ID</th>
              <th>Employee</th>
              <th>Title</th>
              <th>Status</th>
              <th>Category</th>
              <th>Created</th>
              <th>Approved By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customRequests.map((request) => (
              <tr key={`custom-${request.id}`} className={getStatusClass(request.status)}>
                <td className="id-cell">#{request.id}</td>
                <td className="employee-cell">
                  <div className="employee-info">
                    <User size={16} />
                    <div>
                      <div className="employee-name">{request.user?.firstName} {request.user?.lastName}</div>
                      <div className="employee-email">{request.user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="type-cell">{request.title}</td>
                <td className="status-cell">
                  <span className={`status-badge ${getStatusClass(request.status)}`}>
                    {getStatusIcon(request.status)}
                    {request.status}
                  </span>
                </td>
                <td className="purpose-cell"><span className="category-badge">{request.category}</span></td>
                <td className="date-cell">
                  <Calendar size={14} />
                  {formatDate(request.createdAt)}
                </td>
                <td className="approved-by-cell">
                  {request.approvedBy ? (
                    <>{request.approvedBy.firstName} {request.approvedBy.lastName}</>
                  ) : '-'}
                </td>
                <td className="actions-cell">
                  {request.status === 'pending' && (
                    <div className="action-buttons">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleAction(request, 'approve')}
                        disabled={submitting}
                      >
                        <CheckSquare size={14} />
                        Approve
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleAction(request, 'reject')}
                        disabled={submitting}
                      >
                        <XCircle size={14} />
                        Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTemplatesSection = () => {
    // One card per document still offered, under the names the admin gave it
    const offeredTypes = documentTypes.filter((t) => t.active).map((t) => ({
      ...t,
      label: t.nameFr,
      desc: DEFAULT_TEMPLATE_TYPES.find((d) => d.type === t.type)?.desc || 'Document added by the administration',
    }));
    const removedTypes = documentTypes.filter((t) => !t.active);

    return (
      <div className="templates-management-section">
        <div className="section-banner">
          <div>
            <h3>Document Templates (French &amp; English)</h3>
            <p>Each document exists in French and English. Edit it visually, or upload a Word file: its page size and margins are kept. Employee information is inserted from a list, no codes to type.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowTemplateModal(true)}>
            <Upload size={16} /> Upload / Manage Templates
          </button>
        </div>

        <div className="templates-grid">
          {offeredTypes.map((tType) => {
            const uploaded = templatesList.find(t => t.attestationType === tType.type);

            return (
              <div key={tType.type} className={`template-card ${uploaded ? 'active-template' : 'default-template'}`}>
                <div className="template-card-header">
                  <div className="template-icon">
                    <FileCode size={24} />
                  </div>
                  <div className="template-status">
                    <span className={`badge ${uploaded?.htmlContent ? 'badge-success' : 'badge-secondary'}`}>
                      FR {uploaded?.htmlContent ? 'custom' : 'standard'}
                    </span>
                    {' '}
                    <span className={`badge ${uploaded?.htmlContentEn ? 'badge-success' : 'badge-secondary'}`}>
                      EN {uploaded?.htmlContentEn ? 'custom' : 'standard'}
                    </span>
                  </div>
                </div>

                <div className="template-card-body">
                  <h4>{tType.label}</h4>
                  <p className="template-desc" style={{ marginBottom: 4 }}><strong>English:</strong> {tType.nameEn}</p>
                  <p className="template-desc">{tType.desc}</p>

                  {uploaded?.originalFileName || uploaded?.originalFileNameEn ? (
                    <div className="file-info">
                      <strong>Uploaded Word files:</strong>
                      {uploaded.originalFileName && <span className="file-name">FR · {uploaded.originalFileName}</span>}
                      {uploaded.originalFileNameEn && <span className="file-name">EN · {uploaded.originalFileNameEn}</span>}
                    </div>
                  ) : (
                    <div className="file-info default">
                      <span>Using the built-in template. Edit it visually or upload a Word (.docx) file.</span>
                    </div>
                  )}
                </div>

                <div className="template-card-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setEditingTemplate({ type: tType.type, name: tType.label, language: 'fr' });
                      setShowInAppEditorModal(true);
                    }}
                    title="Edit the French version in the visual editor"
                  >
                    <FileEdit size={14} /> Edit FR
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setEditingTemplate({ type: tType.type, name: tType.label, language: 'en' });
                      setShowInAppEditorModal(true);
                    }}
                    title="Edit the English version in the visual editor"
                  >
                    <FileEdit size={14} /> Edit EN
                  </button>

                  {uploaded && (
                    <>
                      {uploaded.originalFileName && (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleDownloadTemplate(tType.type, uploaded.originalFileName, 'fr')}
                          title="Download the uploaded French Word file"
                        >
                          <Download size={14} /> DOCX FR
                        </button>
                      )}
                      {uploaded.originalFileNameEn && (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => handleDownloadTemplate(tType.type, uploaded.originalFileNameEn, 'en')}
                          title="Download the uploaded English Word file"
                        >
                          <Download size={14} /> DOCX EN
                        </button>
                      )}
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteTemplate(tType.type)}
                        title="Go back to the built-in template (French and English)"
                      >
                        <RotateCcw size={14} /> Reset template
                      </button>
                    </>
                  )}

                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setRenamingType({ type: tType.type, nameFr: tType.nameFr, nameEn: tType.nameEn })}
                    title="Change the name of this document in French and English"
                  >
                    <Edit3 size={14} /> Rename
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveType(tType)}
                    title="Employees will no longer be able to request this document"
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {offeredTypes.length === 0 && (
          <div className="file-info default" style={{ marginTop: 12 }}>
            <span>No document can be requested at the moment. Bring one back from the list below.</span>
          </div>
        )}

        {removedTypes.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ margin: '0 0 6px' }}>Removed documents</h4>
            <p className="template-desc">Employees cannot request these any more. Requests already made are kept.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
              {removedTypes.map((docType) => (
                <div
                  key={docType.type}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', border: '1px solid var(--border-color, #e2e8f0)', borderRadius: 10 }}
                >
                  <span><strong>{docType.nameFr}</strong> · {docType.nameEn}</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleRestoreType(docType)}>
                    <RotateCcw size={14} /> Bring back
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="all-requests-page">
      <div className="page-header">
        <div className="header-title">
          <h1>All Requests</h1>
          <p>Manage all attestation, leave, custom requests, and document templates</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setShowTemplateModal(true)}>
            <FileCheck size={16} />
            Manage Templates
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchAllRequests}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="requests-summary">
        <div className="summary-item">
          <span className="summary-label">Total Requests:</span>
          <span className="summary-value">{attestationRequests.length + leaveRequests.length + customRequests.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Attestations:</span>
          <span className="summary-value">{attestationRequests.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Leaves:</span>
          <span className="summary-value">{leaveRequests.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Custom:</span>
          <span className="summary-value">{customRequests.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Active Templates:</span>
          <span className="summary-value generated">{templatesList.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Pending:</span>
          <span className="summary-value pending">
            {attestationRequests.filter(r => r.status === 'pending').length +
             leaveRequests.filter(r => r.status === 'pending').length +
             customRequests.filter(r => r.status === 'pending').length}
          </span>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'attestations' ? 'active' : ''}`}
            onClick={() => setActiveTab('attestations')}
          >
            <FileText size={18} />
            Attestations
            <span className="tab-count">{attestationRequests.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'leaves' ? 'active' : ''}`}
            onClick={() => setActiveTab('leaves')}
          >
            <Calendar size={18} />
            Leave Requests
            <span className="tab-count">{leaveRequests.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'customs' ? 'active' : ''}`}
            onClick={() => setActiveTab('customs')}
          >
            <Edit3 size={18} />
            Custom Requests
            <span className="tab-count">{customRequests.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => setActiveTab('templates')}
          >
            <FileCheck size={18} />
            Document Templates (.docx)
            <span className="tab-count">{templatesList.length}</span>
          </button>
        </div>
      </div>

      <div className="requests-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading requests & templates...</p>
          </div>
        ) : (
          <>
            {activeTab === 'attestations' && renderAttestationRequests()}
            {activeTab === 'leaves' && renderLeaveRequests()}
            {activeTab === 'customs' && renderCustomRequests()}
            {activeTab === 'templates' && renderTemplatesSection()}
          </>
        )}
      </div>

      {/* Full-Screen In-App Visual Editor & Generation Modal */}
      <InAppTemplateEditorModal
        isOpen={showFulfillModal}
        onClose={closeFulfillModal}
        mode="generate"
        requestId={selectedRequest?.id}
        initialLanguage={selectedRequest?.language}
        templateType={selectedRequest?.attestationType || selectedRequest?.type}
        templateName={selectedRequest?.typeLabel || selectedRequest?.attestationType || 'Attestation'}
        employeeLabel={selectedRequest?.user ? `${selectedRequest.user.firstName || ''} ${selectedRequest.user.lastName || ''} (${selectedRequest.user.email || ''})` : ''}
        onGenerate={async ({ html, variables, notes, language }) => {
          if (!selectedRequest) return;
          setSubmitting(true);
          try {
            await api.patch(`/attestations/${selectedRequest.id}/generate`, {
              notes,
              language,
              customValues: variables,
              customHtml: html,
            });
            setMessage({ type: 'success', text: `Attestation personnalisée et générée pour ${selectedRequest.user?.firstName || ''} ${selectedRequest.user?.lastName || ''}!` });
            closeFulfillModal();
            fetchAllRequests();
          } catch (error) {
            console.error('Error fulfilling certificate:', error);
            setMessage({ type: 'error', text: error.message || 'Erreur lors de la génération de l\'attestation.' });
          } finally {
            setSubmitting(false);
          }
        }}
        generating={submitting}
      />


      {renamingType && (
        <div className="modal-overlay" onClick={() => setRenamingType(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Rename document</h2>
            <p>This name is shown to employees when they make a request, and is printed where the template uses the document name.</p>

            <div className="form-group">
              <label>Name in French *</label>
              <input
                type="text"
                value={renamingType.nameFr}
                onChange={(e) => setRenamingType({ ...renamingType, nameFr: e.target.value })}
                maxLength={255}
                placeholder="Attestation de Travail"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Name in English *</label>
              <input
                type="text"
                value={renamingType.nameEn}
                onChange={(e) => setRenamingType({ ...renamingType, nameEn: e.target.value })}
                maxLength={255}
                placeholder="Work Certificate"
              />
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setRenamingType(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleRenameType}
                disabled={!renamingType.nameFr.trim() || !renamingType.nameEn.trim()}
              >
                Save names
              </button>
            </div>
          </div>
        </div>
      )}

      {showApprovalModal && (
        <div className="modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Approve Custom Request</h2>
            <p>Request: {selectedRequest?.title}</p>
            
            <div className="form-group">
              <label>Response Message (Optional)</label>
              <textarea
                value={approvalMessage}
                onChange={(e) => setApprovalMessage(e.target.value)}
                placeholder="Add a message to the requester..."
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Attach File (Optional)</label>
              <input
                type="file"
                onChange={(e) => setApprovalFile(e.target.files[0])}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              {approvalFile && (
                <p className="file-name">Selected: {approvalFile.name}</p>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                  setApprovalMessage('');
                  setApprovalFile(null);
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleApproveCustomRequest}
                disabled={submitting}
              >
                {submitting ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      <TemplateManagementModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          fetchAllRequests();
        }}
      />

      <InAppTemplateEditorModal
        isOpen={showInAppEditorModal}
        onClose={() => setShowInAppEditorModal(false)}
        templateType={editingTemplate?.type}
        templateName={editingTemplate?.name}
        initialLanguage={editingTemplate?.language}
        onSaveSuccess={() => {
          fetchAllRequests();
        }}
      />
    </div>
  );
};

export default AllRequests;
